import { useState, useCallback, useEffect } from 'react';

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  loading: boolean;
  error: string | null;
  permissionDenied: boolean;
}

interface CachedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

const CACHE_KEY = 'nonna_geolocation_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

const getCachedLocation = (): CachedLocation | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedLocation = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - parsed.timestamp < CACHE_DURATION) {
      return parsed;
    }
    
    // Cache expired
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch {
    return null;
  }
};

const setCachedLocation = (latitude: number, longitude: number) => {
  try {
    const cached: CachedLocation = {
      latitude,
      longitude,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    loading: false,
    error: null,
    permissionDenied: false,
  });

  // Check for cached location on mount
  useEffect(() => {
    const cached = getCachedLocation();
    if (cached) {
      setState({
        latitude: cached.latitude,
        longitude: cached.longitude,
        loading: false,
        error: null,
        permissionDenied: false,
      });
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Geolocation is not supported by your browser',
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Cache the location
        setCachedLocation(latitude, longitude);
        
        setState({
          latitude,
          longitude,
          loading: false,
          error: null,
          permissionDenied: false,
        });
      },
      (error) => {
        let errorMessage = 'Unable to get your location';
        let permissionDenied = false;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            permissionDenied = true;
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        setState({
          latitude: null,
          longitude: null,
          loading: false,
          error: errorMessage,
          permissionDenied,
        });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: CACHE_DURATION,
      }
    );
  }, []);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setState({
      latitude: null,
      longitude: null,
      loading: false,
      error: null,
      permissionDenied: false,
    });
  }, []);

  return {
    ...state,
    requestLocation,
    clearLocation,
    hasLocation: state.latitude !== null && state.longitude !== null,
  };
};
